import { useEffect, useRef } from 'react'
import type { NightWindow } from '../city/NightWindow'
import type { MetroMapState } from './metroMapStore'
import { useMetroMapStore } from './useMetroMapStore'
import { useNightModeActions } from './nightModeActions'
import { decideNightMode, nightOverrideUntil } from './nightAutoSwitch'

/** The longest single wait: timers of a sleeping device drift, so look again at least hourly. */
const MAX_WAIT_MS = 60 * 60 * 1000

export interface NightAutoSwitchControllerProps {
  readonly nightWindow: NightWindow
  readonly timeZone: string
  /** Test seam for the clock. */
  readonly now?: () => Date
}

/** A route is being built or shown: flipping the mode would re-filter it away. */
function routeBusy(state: MetroMapState): boolean {
  return state.routeStatus.kind === 'loading' || state.routeStatus.kind === 'results'
}

const systemNow = (): Date => new Date()

/**
 * Turns night mode on inside the city's night window and off outside it
 * (TASK-1136, a city whose `nightWindow.autoSwitch` is set): on page load,
 * when the tab becomes visible again, and at the window's opening or close
 * while the page stays open. A mode change it did not make itself — the
 * toggle, a deep link — is a manual choice and holds until the next opening
 * or close (`decideNightMode`). While a route is building or shown the
 * switch waits (the toggle is hidden then too) and runs once it is closed.
 * Mounted once at AppShell. Renders nothing.
 */
export function NightAutoSwitchController({
  nightWindow,
  timeZone,
  now = systemNow,
}: NightAutoSwitchControllerProps): null {
  const store = useMetroMapStore()
  const actions = useNightModeActions()
  const actionsRef = useRef(actions)
  useEffect(() => {
    actionsRef.current = actions
  })
  const overrideUntilRef = useRef<number | null>(null)

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    let switching = false

    const evaluate = (): void => {
      clearTimeout(timer)
      timer = undefined
      const state = store.getState()
      if (routeBusy(state)) return // re-run when the route closes (subscription below)
      const at = now()
      const decision = decideNightMode(at, nightWindow, timeZone, overrideUntilRef.current)
      overrideUntilRef.current = decision.overrideUntil
      const wait = Math.min(Math.max(decision.nextCheckAt - at.getTime(), 0), MAX_WAIT_MS)
      timer = setTimeout(evaluate, wait)
      if (decision.mode === null || decision.mode === state.currentMode) return
      switching = true
      try {
        if (decision.mode === 'night') actionsRef.current.enableNightMode()
        else actionsRef.current.disableNightMode()
      } finally {
        switching = false
      }
    }

    let lastMode = store.getState().currentMode
    let lastBusy = routeBusy(store.getState())
    const unsubscribe = store.subscribe(() => {
      const state = store.getState()
      if (state.currentMode !== lastMode) {
        lastMode = state.currentMode
        if (!switching) overrideUntilRef.current = nightOverrideUntil(now(), nightWindow, timeZone)
      }
      const busy = routeBusy(state)
      if (busy !== lastBusy) {
        lastBusy = busy
        if (!busy) evaluate()
      }
    })
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') evaluate()
    }
    document.addEventListener('visibilitychange', onVisibility)
    evaluate()

    return () => {
      clearTimeout(timer)
      unsubscribe()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [store, nightWindow, timeZone, now])

  return null
}
